import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { type CanActivateFn, type CanMatchFn, Router, type UrlMatcher } from '@angular/router';
import { isLabEntrySlug } from '@lite-workspace/dto-user';
import { IDENTITY_DATA, IdentityStore } from '@lite-workspace/shared/identity';
import { firstValueFrom } from 'rxjs';

/** A lab's entry link is one segment, `/<slug>`; app, gateway and asset heads never reach the lookup. */
export const labEntryMatcher: UrlMatcher = segments =>
  segments.length === 1 && isLabEntrySlug(segments[0].path) ? { consumed: segments } : null;

// The slug outlives a login whose return cookie died: the cabinet replays it at the first authorized load.
export const PENDING_LAB_ENTRY_KEY = 'pb-pending-lab-entry';
export const PENDING_LAB_ENTRY_TTL_MS = 60 * 60 * 1000;

const forgetPendingLabEntry = (): void => localStorage.removeItem(PENDING_LAB_ENTRY_KEY);

/** Active lab → `/<head>/<key>`, the product's mark route; inactive lab or lookup down → home; no such lab → not-found. */
export const labEntryResolve =
  (head: 'partner' | 'lab'): CanMatchFn =>
  async (_route, segments, current) => {
    const router = inject(Router);
    const http = inject(HttpClient);
    const slug = segments[0].path;
    const home = router.createUrlTree(['/']);
    try {
      const { partnerKey } = await firstValueFrom(http.get<{ partnerKey: string | null }>(`/api/print-services/entry/${slug}`));
      if (!partnerKey) {
        forgetPendingLabEntry();
        return home;
      }

      localStorage.setItem(PENDING_LAB_ENTRY_KEY, JSON.stringify({ slug, at: Date.now() }));
      return router.createUrlTree([`/${head}`, partnerKey], {
        queryParams: current.queryParams,
        fragment: current.fragment ?? undefined,
      });
    } catch (error) {
      // Only a real 404 is «no such lab»: a server error or offline must not strand a lab client on the not-found page.
      if (error instanceof HttpErrorResponse && error.status === 404) {
        forgetPendingLabEntry();
        return false;
      }

      return home;
    }
  };

/** Shell entry: a lab link younger than an hour opens once more, now with a session; the record goes either way. */
export const pendingLabEntryGuard: CanActivateFn = () => {
  const slug = readPendingSlug();
  forgetPendingLabEntry();
  return slug ? inject(Router).createUrlTree(['/', slug]) : true;
};

function readPendingSlug(): string | null {
  try {
    const { slug, at } = JSON.parse(localStorage.getItem(PENDING_LAB_ENTRY_KEY) ?? 'null') ?? {};
    return typeof slug === 'string' && isLabEntrySlug(slug) && Date.now() - at < PENDING_LAB_ENTRY_TTL_MS ? slug : null;
  } catch {
    return null;
  }
}

/** A print lab's entry link marks an unmarked account with that lab, then opens home; a mark never moves. */
export const partnerMarkGuard: CanActivateFn = async ({ paramMap }) => {
  const key = paramMap.get('key');
  const home = inject(Router).createUrlTree(['/']);
  forgetPendingLabEntry();
  if (!key) return home;

  const data = inject(IDENTITY_DATA);
  const identity = inject(IdentityStore);
  // A failed mark must not strand the photographer on the link: home opens either way.
  await firstValueFrom(data.getProfile())
    .then(profile => (profile.partnerKey ? undefined : identity.saveProfile({ partnerKey: key })))
    .catch(() => undefined);
  return home;
};

/** A print lab's account works in that lab's catalog only: its own-product pages send it home. A failed read lets it in. */
export const ownCatalogGuard: CanMatchFn = async () => {
  const router = inject(Router);
  const known = inject(IdentityStore).profile();
  const data = inject(IDENTITY_DATA);
  const profile = known ?? (await firstValueFrom(data.getProfile()).catch(() => null));
  return profile?.partnerKey ? router.createUrlTree(['/']) : true;
};
